import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { useLanguage } from "../i18n";

export default function Header() {
  const { lang, setLang, t } = useLanguage();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  const links = [
    { to: "/", label: t("nav.home"), id: "home" },
    { to: "/about", label: t("nav.about"), id: "about" },
    { to: "/services", label: t("nav.services"), id: "services" },
    { to: "/contact", label: t("nav.contact"), id: "contact" },
  ];

  const LangSwitch = ({ mobile = false }) => (
    <div className="flex items-center gap-1 text-xs font-bold tracking-widest" data-testid={mobile ? "lang-switch-mobile" : "lang-switch"}>
      <button
        data-testid={mobile ? "lang-switch-en-mobile" : "lang-switch-en"}
        onClick={() => setLang("en")}
        className={`px-2 py-1 transition-colors ${lang === "en" ? "text-brand-yellow" : "text-white/40 hover:text-white"}`}
      >
        EN
      </button>
      <span className="text-white/20">/</span>
      <button
        data-testid={mobile ? "lang-switch-it-mobile" : "lang-switch-it"}
        onClick={() => setLang("it")}
        className={`px-2 py-1 transition-colors ${lang === "it" ? "text-brand-yellow" : "text-white/40 hover:text-white"}`}
      >
        IT
      </button>
    </div>
  );

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 border-b transition-colors duration-500 ${
        scrolled ? "border-white/10 bg-black/70 backdrop-blur-xl" : "border-transparent bg-transparent"
      }`}
    >
      <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-6 md:px-12">
        <Link to="/" data-testid="header-logo-link" className="flex items-center">
          <img src="/logo.webp" alt="GTECH SRL" className="h-12 w-auto md:h-14" />
        </Link>

        <nav className="hidden items-center gap-8 lg:flex" data-testid="main-nav">
          {links.map((l) => (
            <NavLink
              key={l.id}
              to={l.to}
              data-testid={`nav-link-${l.id}`}
              className={({ isActive }) =>
                `relative text-sm font-medium tracking-wide transition-colors ${
                  isActive ? "text-brand-yellow" : "text-white/70 hover:text-white"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden items-center gap-6 lg:flex">
          <LangSwitch />
          <Link
            to="/contact"
            data-testid="header-cta-quote"
            className="group flex items-center gap-2 bg-brand-yellow px-5 py-2.5 text-sm font-bold uppercase tracking-wider text-brand-black transition-colors hover:bg-yellow-300"
          >
            {t("header.cta")}
            <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </div>

        <div className="flex items-center gap-4 lg:hidden">
          <LangSwitch mobile />
          <button
            data-testid="mobile-menu-button"
            onClick={() => setOpen(!open)}
            className="text-white"
            aria-label="Toggle menu"
          >
            {open ? <X className="h-7 w-7" /> : <Menu className="h-7 w-7" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden border-t border-white/10 bg-black/95 backdrop-blur-xl lg:hidden"
          >
            <div className="flex flex-col gap-1 px-6 py-6">
              {links.map((l, i) => (
                <motion.div
                  key={l.id}
                  initial={{ opacity: 0, x: -16 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i }}
                >
                  <NavLink
                    to={l.to}
                    data-testid={`mobile-nav-link-${l.id}`}
                    className={({ isActive }) =>
                      `block py-3 font-display text-2xl font-bold tracking-tight ${
                        isActive ? "text-brand-yellow" : "text-white"
                      }`
                    }
                  >
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
              <Link
                to="/contact"
                data-testid="mobile-cta-quote"
                className="mt-4 flex items-center justify-center gap-2 bg-brand-yellow px-5 py-4 text-sm font-bold uppercase tracking-wider text-brand-black"
              >
                {t("header.cta")}
                <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
